import React, { useState, useEffect, useCallback } from 'react';
import { format, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, startOfWeek } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Plus, Loader2, Video, ClipboardList } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { emrService, type EmrAppointment, type EmrServiceType } from '@/services/emrService';
import { practitionerService } from '@/services/practitionerService';
import { calendarService, type CalendarEvent } from '@/services/calendarService';
import { taskService, type Task } from '@/services/taskService';
import DayView from './calendar/DayView';
import WeekView from './calendar/WeekView';
import MonthView from './calendar/MonthView';
import AppointmentWizard from './AppointmentWizard';
import TaskDetailModal from './calendar/TaskDetailModal';

type ViewType = 'Day' | 'Week' | 'Month';

interface EmrCalendarProps {
  clinicId: string;
}

const EmrCalendar: React.FC<EmrCalendarProps> = ({ clinicId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<ViewType>('Month');
  const [appointments, setAppointments] = useState<EmrAppointment[]>([]);
  const [externalEvents, setExternalEvents] = useState<CalendarEvent[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [privacyMode, setPrivacyMode] = useState<'names' | 'initials'>('names');
  const [telehealthOnly, setTelehealthOnly] = useState<boolean>(false);
  const [showTasks, setShowTasks] = useState<boolean>(false);

  // Data for filters
  const [practitioners, setPractitioners] = useState<{ id: string; full_name: string }[]>([]);

  // Modal state
  const [appointmentSheetOpen, setAppointmentSheetOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<EmrAppointment | null>(null);
  const [defaultDate, setDefaultDate] = useState<string>('');
  const [defaultTime, setDefaultTime] = useState<string>('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);

  const { toast } = useToast();

  // Load appointments with proper date range based on current view
  const loadAppointments = useCallback(async () => {
    if (!clinicId) return;
    
    setIsLoading(true);
    try {
      const filters: Record<string, string> = {};
      if (providerFilter !== 'all') filters.providerId = providerFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;

      // Add date range filter based on current view for better performance
      // Get a reasonable date range around the current view (2 months before and after)
      const rangeStart = new Date(currentDate);
      rangeStart.setMonth(rangeStart.getMonth() - 2);
      rangeStart.setDate(1);
      rangeStart.setHours(0, 0, 0, 0);
      
      const rangeEnd = new Date(currentDate);
      rangeEnd.setMonth(rangeEnd.getMonth() + 2);
      rangeEnd.setDate(0); // Last day of month
      rangeEnd.setHours(23, 59, 59, 999);

      filters.startDate = rangeStart.toISOString();
      filters.endDate = rangeEnd.toISOString();

      const taskParams: Record<string, string> = {
        due_date_start: format(rangeStart, 'yyyy-MM-dd'),
        due_date_end: format(rangeEnd, 'yyyy-MM-dd'),
        limit: '500',
      };
      if (providerFilter !== 'all') taskParams.assigned_to = providerFilter;

      const [data, extEventsRes, tasksRes] = await Promise.all([
        emrService.getAppointments(clinicId, filters),
        calendarService.getEvents(
          providerFilter !== 'all' ? providerFilter : undefined,
          rangeStart,
          rangeEnd,
        ).catch(() => ({ events: [] as CalendarEvent[], count: 0, success: false })),
        showTasks
          ? taskService.getTasks(taskParams as any).catch(() => ({ data: { tasks: [] } }))
          : Promise.resolve({ data: { tasks: [] } }),
      ]);

      // Filter for telehealth only if enabled
      let filteredData = data;
      if (telehealthOnly) {
        filteredData = data.filter(apt => apt.visit_mode === 'video');
      }

      setAppointments(filteredData);
      setExternalEvents(extEventsRes.events || []);
      setTasks((tasksRes as any)?.data?.tasks || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load appointments',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [clinicId, currentDate, providerFilter, statusFilter, telehealthOnly, showTasks, toast]);

  // Load filter data
  useEffect(() => {
    const loadFilterData = async () => {
      if (!clinicId) return;

      try {
        const practitionersRes = await practitionerService.getPractitionerByClinicId(clinicId, { limit: 100 });
        // Map practitioners to a simpler format for the dropdown
        const practitioners = practitionersRes?.practitioners || practitionersRes || [];
        setPractitioners(practitioners.map((p: { id: string; first_name: string; last_name: string; title?: string }) => ({
          id: p.id,
          full_name: `${p.first_name} ${p.last_name}${p.title ? `, ${p.title}` : ''}`
        })));
      } catch (error) {
        console.error('Error loading filter data:', error);
      }
    };

    loadFilterData();
  }, [clinicId]);

  // Load appointments when filters change
  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Navigation
  const goToToday = () => setCurrentDate(new Date());

  const goToPrevious = () => {
    if (view === 'Day') setCurrentDate(d => subDays(d, 1));
    else if (view === 'Week') setCurrentDate(d => subWeeks(d, 1));
    else setCurrentDate(d => subMonths(d, 1));
  };

  const goToNext = () => {
    if (view === 'Day') setCurrentDate(d => addDays(d, 1));
    else if (view === 'Week') setCurrentDate(d => addWeeks(d, 1));
    else setCurrentDate(d => addMonths(d, 1));
  };

  // Handlers
  const handleCreateAppointment = (date?: string, time?: string) => {
    setSelectedAppointment(null);
    setDefaultDate(date || format(new Date(), 'yyyy-MM-dd'));
    setDefaultTime(time || format(new Date(), 'HH:mm'));
    setAppointmentSheetOpen(true);
  };

  const handleEditAppointment = (appointment: EmrAppointment) => {
    setSelectedAppointment(appointment);
    setAppointmentSheetOpen(true);
  };

  const handleTimeSlotClick = (date: string, time: string) => {
    handleCreateAppointment(date, time);
  };

  const handleDayClick = (date: Date) => {
    handleCreateAppointment(format(date, 'yyyy-MM-dd'), format(new Date(), 'HH:mm'));
  };

  const handleAppointmentSaved = () => {
    loadAppointments();
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setTaskDetailOpen(true);
  };

  const handleTaskUpdated = () => {
    loadAppointments();
  };

  // Get header title based on view
  const getHeaderTitle = () => {
    if (view === 'Day') return format(currentDate, 'MMMM d, yyyy');
    if (view === 'Week') {
      const weekStart = startOfWeek(currentDate);
      return format(weekStart, 'MMMM yyyy');
    }
    return format(currentDate, 'MMMM yyyy');
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Toolbar */}
      <div className="flex-shrink-0 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
        {/* Top row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={goToToday} className="h-8 text-xs font-medium">
            Today
          </Button>

          <div className="flex items-center">
            <Button variant="ghost" size="icon" onClick={goToPrevious} className="h-8 w-8 text-slate-500 hover:text-slate-900">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={goToNext} className="h-8 w-8 text-slate-500 hover:text-slate-900">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <h2 className="font-montserrat text-base font-semibold text-slate-900 sm:text-lg">{getHeaderTitle()}</h2>

          {/* View toggle - matches tab pattern from design system */}
          <div className="ml-auto flex items-center gap-0.5 rounded-lg border border-slate-200 bg-[#f8f3ed] p-0.5">
            {(['Day', 'Week', 'Month'] as const).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  view === v
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          <Button onClick={() => handleCreateAppointment()} size="sm" className="ml-2 h-9">
            <Plus className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">New Appointment</span>
          </Button>
        </div>

        {/* Filter row */}
        <div className="flex items-center gap-2 mt-3 overflow-x-auto">
          <Button
            variant={telehealthOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              const next = !telehealthOnly;
              setTelehealthOnly(next);
              if (next) setShowTasks(false);
            }}
            className={`flex-shrink-0 h-8 text-xs ${telehealthOnly ? '' : ''}`}
          >
            <Video className="h-3.5 w-3.5 mr-1.5" />
            Video
          </Button>

          <Button
            variant={showTasks ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              const next = !showTasks;
              setShowTasks(next);
              if (next) setTelehealthOnly(false);
            }}
            className={`flex-shrink-0 h-8 text-xs ${showTasks ? 'bg-orange-500 hover:bg-orange-600 border-orange-500' : ''}`}
          >
            <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
            Tasks
          </Button>

          <Select value={providerFilter} onValueChange={setProviderFilter}>
            <SelectTrigger className="w-[130px] sm:w-[160px] h-8 text-xs flex-shrink-0 border-slate-200">
              <SelectValue placeholder="All Providers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Providers</SelectItem>
              {practitioners.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[120px] sm:w-[140px] h-8 text-xs flex-shrink-0 border-slate-200">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="checked_in">Checked In</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="no_show">No-show</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={privacyMode}
            onValueChange={(v: 'names' | 'initials') => setPrivacyMode(v)}
          >
            <SelectTrigger className="w-[120px] sm:w-[140px] h-8 text-xs flex-shrink-0 border-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="names">Show Names</SelectItem>
              <SelectItem value="initials">Show Initials</SelectItem>
            </SelectContent>
          </Select>

          {/* Status colour legend */}
          <div className="ml-auto hidden items-center gap-3 md:flex flex-shrink-0">
            {[
              { label: 'Scheduled', color: '#3b82f6' },
              { label: 'Confirmed', color: '#22c55e' },
              { label: 'Checked In', color: '#f59e0b' },
              { label: 'In Progress', color: '#8b5cf6' },
              { label: 'Completed', color: '#6b7280' },
              { label: 'No Show', color: '#ef4444' },
              { label: 'Cancelled', color: '#9ca3af' },
              ...(showTasks ? [{ label: 'Task', color: '#f97316' }] : []),
            ].map(({ label, color }) => (
              <div key={label} className="flex items-center gap-1 flex-shrink-0">
                <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <span className="text-[10px] text-slate-500 whitespace-nowrap">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar View */}
      <div className="flex-1 overflow-auto bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-brand-teal" />
          </div>
        ) : (
          <>
            {view === 'Day' && (
              <DayView
                date={currentDate}
                appointments={showTasks ? [] : appointments}
                externalEvents={showTasks ? [] : externalEvents}
                tasks={showTasks ? tasks : []}
                onAppointmentClick={handleEditAppointment}
                onTimeSlotClick={handleTimeSlotClick}
                onTaskClick={handleTaskClick}
              />
            )}

            {view === 'Week' && (
              <WeekView
                weekStart={startOfWeek(currentDate)}
                appointments={showTasks ? [] : appointments}
                externalEvents={showTasks ? [] : externalEvents}
                tasks={showTasks ? tasks : []}
                onAppointmentClick={handleEditAppointment}
                onTimeSlotClick={handleTimeSlotClick}
                onTaskClick={handleTaskClick}
              />
            )}

            {view === 'Month' && (
              <MonthView
                currentMonth={currentDate}
                appointments={showTasks ? [] : appointments}
                externalEvents={showTasks ? [] : externalEvents}
                tasks={showTasks ? tasks : []}
                onAppointmentClick={handleEditAppointment}
                onDayClick={handleDayClick}
                onTaskClick={handleTaskClick}
                privacyMode={privacyMode}
              />
            )}
          </>
        )}
      </div>

      {/* Appointment Wizard */}
      <AppointmentWizard
        open={appointmentSheetOpen}
        onOpenChange={setAppointmentSheetOpen}
        clinicId={clinicId}
        appointment={selectedAppointment}
        defaultDate={defaultDate}
        defaultTime={defaultTime}
        onSave={handleAppointmentSaved}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        open={taskDetailOpen}
        onOpenChange={setTaskDetailOpen}
        onTaskUpdated={handleTaskUpdated}
      />
    </div>
  );
};

export default EmrCalendar;
